import { useEffect, useState, useRef, useMemo } from "react";
import { useGlobalContext } from "../../data/GlobalContext";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { launchGameByName } from "../../data/launchGame";
import { initialStages, TEST_TURNOVER_OVERRIDES } from "./jili-data";
import JiliQuestTNC from "./JiliQuestTNC";
import { promoURL } from "../../../App";


const LazyImage = ({ src, alt = "", ...props }) => {
	return <img src={src} alt={alt} loading="lazy" decoding="async" {...props} />;
};

const getTurnoverKey = (stageId) => {
	const week = Math.ceil(stageId / 5);
	const stageIndex = (stageId - 1) % 5;

	const letter = String.fromCharCode(97 + stageIndex);

	return `week${week}${letter}`;
};

function JiliQuest({ onClose }) {
	const [showMission, setIsShowMission] = useState(false);
	const [isTNC, setIsTNC] = useState(false);
	const [loading, setIsLoading] = useState(true);
	const [currentWeek, setCurrentWeek] = useState(1);
	const [isWeekTransitioning, setIsWeekTransitioning] = useState(false);
	const [transitionPhase, setTransitionPhase] = useState("complete");
	const [transitionWeek, setTransitionWeek] = useState(null);
	const [turnoverData, setTurnoverData] = useState({});
	const [turnoverLoading, setTurnoverLoading] = useState(false);
	const [, setTurnoverError] = useState(null);
	const [, setClaimResults] = useState({});

	const autoAdvanceWeeks = useRef(new Set());

	const claimedStagesRef = useRef(new Set());
	const claimingStagesRef = useRef(new Set());

	const { allGamesFlat, tokenUrl, launchUrl, username } = useGlobalContext();

	const isLoggedIn = Boolean(username);

	const [unlockingStageId, setUnlockingStageId] = useState(null);
	const [isSoundOn, setIsSoundOn] = useState(() => {
		const savedSoundState = localStorage.getItem("jiliQuestSoundOn");

		return savedSoundState === null ? true : savedSoundState === "true";
	});

	const previousLockStateRef = useRef(new Map());

	const location = useLocation();

	const navigate = useNavigate();
	const audioRef = useRef(null);

	useEffect(() => {
		if (!username) {
			return;
		}

		let requestInFlight = false;

		const fetchJiliQuestTurnover = async () => {
			if (requestInFlight) {
				return;
			}

			requestInFlight = true;

			try {
				setTurnoverLoading(true);
				setTurnoverError(null);

				const url =
					`${promoURL}` +
					`?cmd=jiliQuest` +
					`&u=${encodeURIComponent(username)}` +
					`&_=${Date.now()}`;

				const response = await fetch(url, {
					method: "GET",
					cache: "no-store",
				});

				if (!response.ok) {
					throw new Error(`HTTP status ${response.status}`);
				}

				const data = await response.json();

				console.log("[JILI Quest] Turnover response:", data);

				const normalizedData = Object.fromEntries(
					Object.entries(data ?? {}).map(([key, value]) => [
						key,
						Number(value) || 0,
					]),
				);

				setTurnoverData(normalizedData);
			} catch (error) {
				console.error("[JILI Quest] Error fetching turnover:", error);

				setTurnoverError(error);
			} finally {
				requestInFlight = false;
				setTurnoverLoading(false);
			}
		};

		fetchJiliQuestTurnover();

		const turnoverInterval = setInterval(() => {
			fetchJiliQuestTurnover();
		}, 15000);

		const handleWindowFocus = () => {
			fetchJiliQuestTurnover();
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				fetchJiliQuestTurnover();
			}
		};

		window.addEventListener("focus", handleWindowFocus);

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			clearInterval(turnoverInterval);

			window.removeEventListener("focus", handleWindowFocus);

			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [username]);

	const stages = useMemo(() => {
		let previousStagesCompleted = true;

		return initialStages.map((stage) => {
			const turnoverKey = getTurnoverKey(stage.id);

			const currentTurnover = Number(turnoverData[turnoverKey] ?? 0);

			// For Testing Purposes
			// const currentTurnover = Number(
			// 	TEST_TURNOVER_OVERRIDES[turnoverKey] ?? turnoverData[turnoverKey] ?? 0,
			// );

			const locked = !previousStagesCompleted;

			const completed = !locked && currentTurnover >= stage.requiredTurnover;

			const status = completed
				? "COMPLETED"
				: locked
					? "LOCKED"
					: "IN PROGRESS";

			if (!completed) {
				previousStagesCompleted = false;
			}

			return {
				...stage,
				turnoverKey,
				currentTurnover,
				locked,
				status,
			};
		});
	}, [turnoverData]);

	useEffect(() => {
		const previousLockStates = previousLockStateRef.current;

		if (previousLockStates.size === 0) {
			stages.forEach((stage) => {
				previousLockStates.set(stage.id, stage.locked);
			});

			return;
		}

		const newlyUnlockedStage = stages.find(
			(stage) =>
				previousLockStates.get(stage.id) === true && stage.locked === false,
		);

		stages.forEach((stage) => {
			previousLockStates.set(stage.id, stage.locked);
		});

		if (!newlyUnlockedStage) {
			return;
		}

		setUnlockingStageId(newlyUnlockedStage.id);

		const timer = setTimeout(() => {
			setUnlockingStageId(null);
		}, 1000);

		return () => clearTimeout(timer);
	}, [stages]);

	useEffect(() => {
		if (!username || turnoverLoading) {
			return;
		}

		const completedStages = stages.filter(
			(stage) => stage.status === "COMPLETED",
		);

		if (completedStages.length === 0) {
			return;
		}

		const claimCompletedStages = async () => {
			for (const stage of completedStages) {
				const stageKey = stage.turnoverKey;

				if (
					claimedStagesRef.current.has(stageKey) ||
					claimingStagesRef.current.has(stageKey)
				) {
					continue;
				}

				claimingStagesRef.current.add(stageKey);

				try {
					const url =
						`${promoURL}` +
						`?cmd=claimJiliQuest` +
						`&stage=${encodeURIComponent(stageKey)}` +
						`&u=${encodeURIComponent(username)}`;

					console.log(`[JILI Quest] Claiming ${stageKey}:`, url);

					const response = await fetch(url);

					if (!response.ok) {
						throw new Error(`HTTP status ${response.status}`);
					}

					const data = await response.json();

					console.log(`[JILI Quest] Claim response for ${stageKey}:`, data);

					if (data?.s === "s") {
						claimedStagesRef.current.add(stageKey);

						setClaimResults((prev) => ({
							...prev,
							[stageKey]: {
								status: "SUCCESS",
								message: "Reward credited.",
							},
						}));

						console.log(`[JILI Quest] Reward credited for ${stageKey}`);

						continue;
					}

					if (data?.e === "Already claimed.") {
						claimedStagesRef.current.add(stageKey);

						setClaimResults((prev) => ({
							...prev,
							[stageKey]: {
								status: "ALREADY_CLAIMED",
								message: data.e,
							},
						}));

						console.log(`[JILI Quest] ${stageKey} already claimed.`);

						continue;
					}

					throw new Error(data?.e || "Unknown claim response");
				} catch (error) {
					console.error(`[JILI Quest] Failed claiming ${stageKey}:`, error);

					setClaimResults((prev) => ({
						...prev,
						[stageKey]: {
							status: "ERROR",
							message: error.message,
						},
					}));
				} finally {
					claimingStagesRef.current.delete(stageKey);
				}
			}
		};

		claimCompletedStages();
	}, [stages, username, turnoverLoading]);

	const STAGES_PER_WEEK = 5;

	const totalWeeks = Math.ceil(stages.length / STAGES_PER_WEEK);

	const mobileWeekChoices = [currentWeek - 1, currentWeek + 1].filter(
		(week) => week >= 1 && week <= totalWeeks,
	);

	const visibleStages = stages.slice(
		(currentWeek - 1) * STAGES_PER_WEEK,
		currentWeek * STAGES_PER_WEEK,
	);

	const isCurrentWeekCompleted =
		visibleStages.length === STAGES_PER_WEEK &&
		visibleStages.every((stage) => stage.status === "COMPLETED");

	useEffect(() => {
		if (
			!isCurrentWeekCompleted ||
			currentWeek >= totalWeeks ||
			autoAdvanceWeeks.current.has(currentWeek)
		) {
			return;
		}

		const completedWeek = currentWeek;
		const nextWeek = completedWeek + 1;

		autoAdvanceWeeks.current.add(completedWeek);

		setTransitionWeek(completedWeek);
		setTransitionPhase("complete");
		setIsWeekTransitioning(true);

		const unlockTimer = setTimeout(() => {
			setTransitionPhase("unlocking");
		}, 1800);

		const finishTimer = setTimeout(() => {
			setIsWeekTransitioning(false);
			setTransitionPhase("complete");
			setTransitionWeek(null);

			setCurrentWeek(nextWeek);
		}, 3600);

		return () => {
			clearTimeout(unlockTimer);
			clearTimeout(finishTimer);
		};
	}, [isCurrentWeekCompleted, currentWeek, totalWeeks]);

	const earnedRewards = stages
		.filter((stage) => stage.status === "COMPLETED")
		.reduce(
			(total, stage) => {
				stage.rewards.forEach((reward) => {
					if (reward.type === "FREE_SPINS") {
						total.freeSpins += reward.amount;
					}

					if (reward.type === "FREE_CREDITS") {
						total.freeCredits += reward.amount;
					}
				});

				return total;
			},
			{
				freeSpins: 0,
				freeCredits: 0,
			},
		);

	useEffect(() => {
		const loadingTimer = setTimeout(() => {
			setIsLoading(false);
		}, 3000);

		return () => clearTimeout(loadingTimer);
	}, []);

	const handleOpenTNC = () => {
		setIsTNC(true);
	};

	const currentMission =
		[...visibleStages].reverse().find((stage) => !stage.locked) ??
		visibleStages[0];

	const currentStageId =
		currentMission?.status === "IN PROGRESS" ? currentMission.id : null;

	const progress = currentMission
		? Math.min(
				100,
				(currentMission.currentTurnover / currentMission.requiredTurnover) *
					100,
			)
		: 0;
	const handleStageClick = (stage) => {
		if (stage.locked) {
			return;
		}

		launchGameByName({
			provider: stage.provider,
			gameName: stage.gameName,
			username,
			tokenUrl,
			launchUrl,
			allGamesFlat,
			location,
		});
	};

	const handleShowMission = () => {
		setIsShowMission(true);
	};

	const handleCloseJiliQuest = () => {
		onClose?.();
	};

	const handleGoHome = () => {
		onClose?.();
		navigate("/promotion");
	};

	const handleToggleSound = () => {
		const audio = audioRef.current;

		if (!audio) return;

		setIsSoundOn((prev) => {
			const nextSoundState = !prev;

			if (nextSoundState) {
				audio.play().catch((error) => {
					console.error("[JILI Quest] Unable to play audio:", error);
				});
			} else {
				audio.pause();
			}

			localStorage.setItem("jiliQuestSoundOn", String(nextSoundState));

			return nextSoundState;
		});
	};

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio) return;
		audio.volume = 0.4;
		if (isSoundOn) {
			audio.play().catch((error) => {
				console.error("[JILI Quest] Unable to play audio:", error);
			});
		} else {
			audio.pause();
		}
	}, [isSoundOn]);

	if (!isLoggedIn) {
		return <Navigate to="/promotion/jili-quest" replace />;
	}
	return (
		<>
			{loading ? (
				<div className="jili-quest-loader">
					<picture className="jili-quest-loader__background">
						<source
							media="(max-width: 1024px)"
							srcSet="/images/jili-quest/JILI-QUEST_BG-MOBILE.webp"
						/>

						<img
							className="quest-map__background"
							src="/images/jili-quest/JILI_QUEST_LOADING-BG.webp"
							alt="Quest map"
							loading="eager"
							decoding="async"
							fetchPriority="high"
						/>
					</picture>

					<div className="jili-quest-loader__overlay" />

					<div className="jili-quest-loader__rays" />

					<div className="jili-quest-loader__content">
						<img
							className="jili-quest-loader__logo"
							src="/images/jili-quest/jili-quest-title.webp"
							alt="JILI Quest"
							loading="eager"
							decoding="async"
							fetchPriority="high"
						/>
						<div className="jili-quest-loader__chest-wrapper">
							<div className="jili-quest-loader__glow" />

							<LazyImage src="/images/jili-quest/chest-01.webp" alt="" />

							<span className="jili-quest-loader__sparkle sparkle-loader--1">
								✦
							</span>

							<span className="jili-quest-loader__sparkle sparkle-loader--2">
								✦
							</span>

							<span className="jili-quest-loader__sparkle sparkle-loader--3">
								✦
							</span>

							<span className="jili-quest-loader__sparkle sparkle-loader--4">
								✦
							</span>
						</div>

						<div className="jili-quest-loader__text">ENTERING THE JUNGLE</div>

						<div className="jili-quest-loader__subtext">
							Preparing your quest...
						</div>

						<div className="jili-quest-loader__dots">
							<span />
							<span />
							<span />
						</div>
					</div>
				</div>
			) : (
				<div className="jili-quest">
					<audio ref={audioRef} loop>
						<source
							src="/images/jili-quest/sound/JILI-SOUND.ogg"
							type="audio/ogg"
						/>
						<source
							src="/images/jili-quest/sound/JILI-SOUND-MP.mp3"
							type="audio/mpeg"
						/>
					</audio>
					<div
						className="quest-map"
						style={{
							"--quest-bg": 'url("/images/jili-quest/JILI-QUEST_BG.webp")',
						}}
					>
						<button
							type="button"
							className="jili-quest__minimize"
							title="Minimize"
							onClick={handleCloseJiliQuest}
						>
							{/* <img src="/images/jili-quest/BACK_BTN.webp" alt="" /> */}
						</button>
						<picture>
							<source
								media="(max-width: 1024px)"
								srcSet="/images/jili-quest/JILI-QUEST_BG-MOBILE.webp"
							/>

							<img
								className="quest-map__background"
								src="/images/jili-quest/JILI-QUEST_BG.webp"
								alt="Quest map"
								loading="eager"
								decoding="async"
								fetchPriority="high"
							/>
						</picture>

						<div className="quest-mobile-side-actions">
							<button
								type="button"
								className="jili-quest-home"
								onClick={handleGoHome}
								aria-label="Back to Promotions"
								title="Back to Promotions"
							>
								<img
									src="/images/jili-quest/HOME_ICON.webp"
									alt=""
									aria-hidden="true"
								/>
							</button>
							<button
								type="button"
								className="mission-icon"
								onClick={handleShowMission}
								aria-label="Open current mission"
							>
								<img
									src="/images/jili-quest/MISSION_ICON.webp"
									alt=""
									aria-hidden="true"
								/>
							</button>
							<button
								type="button"
								className="jili-quest-sound"
								onClick={handleToggleSound}
								aria-label={isSoundOn ? "Mute sound" : "Unmute sound"}
								title={isSoundOn ? "Mute sound" : "Unmute sound"}
							>
								<img
									src={
										isSoundOn
											? "/images/jili-quest/JQ_SOUND-ON.webp"
											: "/images/jili-quest/JQ_SOUND-OFF.webp"
									}
									alt=""
									aria-hidden="true"
								/>
							</button>
							<div className="jili-quest--btn-info">
								<button
									type="button"
									onClick={handleOpenTNC}
									aria-label="Open JILI Quest terms and conditions"
								>
									<img
										src="/images/jili-quest/JQ_SCROLL.webp"
										alt="Information"
									/>
								</button>
							</div>
						</div>

						{visibleStages.map((stage) => {
							const stageProgress = Math.min(
								100,
								(stage.currentTurnover / stage.requiredTurnover) * 100,
							);

							const isCompleted = stage.status === "COMPLETED";
							const isUnlocking = unlockingStageId === stage.id;
							const isCurrent = currentStageId === stage.id && !isUnlocking;

							return (
								<button
									key={stage.id}
									type="button"
									className={`stage
        ${stage.locked ? "stage--locked" : ""}
        ${isCompleted ? "stage--completed" : ""}
        ${isCurrent ? "stage--current" : ""}
        ${isUnlocking ? "stage--unlocking" : ""}
      `}
									style={{
										"--x": stage.x,
										"--y": stage.y,
										"--x-mobile": stage.xM ?? stage.x,
										"--y-mobile": stage.yM ?? stage.y,
									}}
									aria-label={
										stage.locked
											? `Stage ${stage.id} is locked`
											: isCompleted
												? `Play completed stage ${stage.id}`
												: `Open stage ${stage.id}`
									}
									disabled={stage.locked || isCompleted}
									onClick={() => handleStageClick(stage)}
								>
									<LazyImage className="stage__logo" src={stage.logo} alt="" />

									{(stage.locked || isUnlocking) && (
										<LazyImage
											className={`game-lock ${
												isUnlocking ? "game-lock--unlocking" : ""
											}`}
											src="/images/jili-quest/GAME-RUSH-LOCK.webp"
											alt=""
											aria-hidden="true"
										/>
									)}
									{!stage.locked && (
										<div className="game-turnover">
											<strong>{stage.currentTurnover.toLocaleString()}</strong>

											<span>
												{" / "}
												{stage.requiredTurnover.toLocaleString()}
											</span>
										</div>
									)}

									{!stage.locked && !isUnlocking && (
										<div
											className={`stage-controls ${
												isCompleted ? "stage-controls--completed" : ""
											}`}
										>
											<div className="stage-turnover">
												<div className="stage-turnover__bar">
													<div
														className="stage-turnover__progress"
														style={{
															width: `${stageProgress}%`,
														}}
													/>

													<span className="stage-turnover__percentage">
														{Math.round(stageProgress)}%
													</span>
												</div>
											</div>
											{!isCompleted && (
												<img
													className="game-btn"
													src="/images/jili-quest/GAME-RUSH-BTN.webp"
													alt=""
													aria-hidden="true"
												/>
											)}
											{isCompleted && (
												<span className="stage-completed-badge">
													<span className="stage-completed-badge__check">
														✓
													</span>
													COMPLETED
												</span>
											)}
										</div>
									)}
								</button>
							);
						})}

						<div className="jili-quest-week-nav">
							<button
								type="button"
								className="jili-quest-week-nav__arrow jili-arrow-left"
								aria-label="Previous week"
								onClick={() =>
									setCurrentWeek((prevWeek) => Math.max(1, prevWeek - 1))
								}
								disabled={currentWeek === 1}
							>
								<span>‹</span>
							</button>

							<div className="jili-quest-week-nav__center">
								<span className="jili-quest-week-nav__small">
									ADVENTURE MAP
								</span>

								<strong className="jili-quest-week-nav__week">
									WEEK {currentWeek}
								</strong>

								<div className="jili-quest-week-nav__dots">
									{Array.from({ length: totalWeeks }).map((_, index) => (
										<span
											key={index}
											className={
												index + 1 === currentWeek
													? "jili-quest-week-nav__dot is-active"
													: "jili-quest-week-nav__dot"
											}
										/>
									))}
								</div>
							</div>

							<button
								type="button"
								className="jili-quest-week-nav__arrow jili-arrow-right"
								aria-label="Next week"
								onClick={() =>
									setCurrentWeek((prevWeek) =>
										Math.min(totalWeeks, prevWeek + 1),
									)
								}
								disabled={currentWeek === totalWeeks}
							>
								<span>›</span>
							</button>
						</div>

						<div className="jili-quest-mobile-week-nav">
							{mobileWeekChoices.map((week) => {
								const isPreviousWeek = week < currentWeek;

								return (
									<button
										key={week}
										type="button"
										className={`jili-quest-mobile-week-nav__button ${
											isPreviousWeek
												? "jili-quest-mobile-week-nav__button--previous"
												: "jili-quest-mobile-week-nav__button--next"
										}`}
										onClick={() => setCurrentWeek(week)}
										aria-label={`Go to Week ${week}`}
									>
										<LazyImage
											src={`/images/jili-quest/JILI_QUEST-WEEK-MOBILE_${week}.webp`}
											alt={`Week ${week}`}
										/>
									</button>
								);
							})}
						</div>

						{isWeekTransitioning && (
							<div className="jili-week-complete">
								<div className="jili-week-complete__leaf jili-week-complete__leaf--1">
									🍃
								</div>

								<div className="jili-week-complete__leaf jili-week-complete__leaf--2">
									🍃
								</div>

								<div className="jili-week-complete__leaf jili-week-complete__leaf--3">
									🍃
								</div>

								<div className="jili-week-complete__leaf jili-week-complete__leaf--4">
									🍃
								</div>

								<div className="jili-week-complete__rays" />

								<div className="jili-week-complete__content">
									<div className="jili-week-complete__chest-wrapper">
										<div className="jili-week-complete__glow" />

										<img
											className="jili-quest-loader__chest"
											src="/images/jili-quest/chest-01.webp"
											alt=""
											loading="eager"
											decoding="async"
										/>

										<span className="jili-week-complete__sparkle sparkle--1">
											✦
										</span>
										<span className="jili-week-complete__sparkle sparkle--2">
											✦
										</span>
										<span className="jili-week-complete__sparkle sparkle--3">
											✦
										</span>
										<span className="jili-week-complete__sparkle sparkle--4">
											✦
										</span>
									</div>

									{transitionPhase === "complete" ? (
										<>
											<div className="jili-week-complete__small-title">
												QUEST COMPLETE
											</div>

											<h2 className="jili-week-complete__title">
												WEEK {transitionWeek}
												<span>COMPLETE!</span>
											</h2>
										</>
									) : (
										<>
											<div className="jili-week-complete__small-title">
												NEW ADVENTURE
											</div>

											<h2 className="jili-week-complete__title jili-week-complete__title--unlock">
												WEEK {transitionWeek + 1}
												<span>UNLOCKED!</span>
											</h2>
										</>
									)}

									<div className="jili-week-complete__loading">
										<span />
										<span />
										<span />
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="jili-quest--title">
						<img
							src="/images/jili-quest/jili-quest-title.webp"
							alt="JILI Quest"
						/>
					</div>
					<div className={`jili-quest--map ${showMission ? "is-open" : ""}`}>
						<div className="jili-guest--mission-title">
							<img src="/images/jili-quest/MISSION_ICON_TITLE.webp" alt="" />
						</div>
						<div className="jili-quest--map--container">
							<div className="mission-card">
								{/* Decorative glow */}
								<div className="mission-card__glow" />

								{/* Week badge */}
								<div className="mission-card--week">
									<img
										src={`/images/jili-quest/JILI_QUEST-WEEK-${currentWeek}.webp`}
										alt={`Week ${currentWeek}`}
									/>
								</div>

								{/* Stage heading */}
								<div className="mission-card__header">
									<div className="stage-title-wrapper">
										<span className="stage-title-decoration" />
										<h1 className="stage-title">Stage {currentMission.id}</h1>
										<span className="stage-title-decoration" />
									</div>
								</div>

								{/* Game */}
								<div className="mission-game">
									<div className="game-icon">
										<LazyImage
											src={currentMission.logo}
											alt={currentMission.gameName}
										/>
									</div>

									<div className="mission-game__details">
										<span className="label">MISSION GAME</span>
										<h3>{currentMission.gameName}</h3>

										<span className="mission-game__hint">
											Play this game to complete the stage
										</span>
									</div>
								</div>

								{/* Turnover */}
								<div className="turnover-panel">
									<div className="turnover-panel__header">
										<div>
											<span className="label">TURNOVER PROGRESS</span>

											<div className="current-turnover">
												<strong>
													{currentMission.currentTurnover.toLocaleString()}
												</strong>

												<span>
													{" / "}
													{currentMission.requiredTurnover.toLocaleString()}
												</span>
											</div>
										</div>

										<div className="turnover-target">
											<div className="turnover-target__icon">
												<LazyImage
													src="/images/jili-quest/turnover.webp"
													alt=""
												/>
											</div>

											<div>
												<span>GOAL</span>
												<strong>
													{currentMission.requiredTurnover.toLocaleString()}
												</strong>
											</div>
										</div>
									</div>

									<div className="progress-wrapper">
										<div
											className="progress-bar"
											style={{
												width: `${Math.min(progress, 100)}%`,
											}}
										>
											<div className="progress-bar__shine" />
										</div>
									</div>

									<div className="progress-meta">
										<span>Stage progress</span>
										<strong>{Math.round(progress)}%</strong>
									</div>
								</div>

								{/* Rewards */}
								<div className="mission-rewards">
									<div className="mission-rewards__heading">
										<span className="mission-rewards__chest">🎁</span>

										<div>
											<span className="label">STAGE REWARD</span>
											<strong>Complete the mission to unlock</strong>
										</div>
									</div>

									<div className="reward-content">
										{currentMission.rewards.map((reward, index) => (
											<div
												className="reward-content__item"
												key={`${reward.type}-${index}`}
											>
												<div className="reward-icon">
													<LazyImage
														src={
															reward.type === "FREE_SPINS"
																? "/images/jili-quest/freespin-01.webp"
																: "/images/jili-quest/free-credits.webp"
														}
														alt=""
													/>
												</div>

												<div className="reward-content__details">
													<span>REWARD</span>
													<strong>{reward.label}</strong>
												</div>
											</div>
										))}
									</div>
								</div>

								{/* Status */}
								<div className="status-row">
									<div className="status-row__label">
										<span className="status-dot" />
										<span>MISSION STATUS</span>
									</div>

									<span
										className={`status-badge status-badge--${currentMission.status
											.toLowerCase()
											.replace(/\s+/g, "-")}`}
									>
										{currentMission.status}
									</span>
								</div>
							</div>

							<div className="reward-container">
								{/* Decorative glow */}
								<div className="reward-container__glow" />

								{/* Header */}
								<div className="reward-header">
									<div className="reward-chest">
										<img
											src="/images/jili-quest/chest-01.webp"
											alt="Reward Chest"
										/>
									</div>

									<div className="reward-header__content">
										<span className="reward-header__eyebrow">
											YOUR ADVENTURE LOOT
										</span>

										<h2>PROMO REWARD CHEST</h2>

										<span className="reward-header__subtitle">
											Rewards earned from completed stages
										</span>
									</div>
								</div>

								{/* Rewards */}
								<div className="reward-list">
									{/* Free Spins */}
									<div className="reward-item">
										<div className="reward-item__icon">
											<LazyImage
												src="/images/jili-quest/freespin-01.webp"
												alt="Free Spins"
											/>
										</div>

										<div className="reward-item__content">
											<span className="reward-label">TOTAL FREE SPINS</span>

											<div className="reward-value">
												<strong>{earnedRewards.freeSpins}</strong>

												<span>
													{earnedRewards.freeSpins === 1
														? "Free Spin"
														: "Free Spins"}
												</span>
											</div>
										</div>
									</div>

									{/* Free Credits */}
									<div className="reward-item">
										<div className="reward-item__icon">
											<LazyImage
												src="/images/jili-quest/free-credits.webp"
												alt="Free Credits"
											/>
										</div>

										<div className="reward-item__content">
											<span className="reward-label">TOTAL FREE CREDITS</span>

											<div className="reward-value">
												<strong>{earnedRewards.freeCredits}</strong>

												<span>
													{earnedRewards.freeCredits === 1
														? "Free Credit"
														: "Free Credits"}
												</span>
											</div>
										</div>
									</div>
								</div>

								{/* Footer */}
								<div className="reward-footer">
									<span className="reward-footer__icon">i</span>

									<span>
										Rewards are credited automatically upon stage completion.
									</span>
								</div>
							</div>
							<div className="mission-close--container">
								<button
									className="mission-close"
									onClick={() => setIsShowMission(false)}
								>
									CLOSE
								</button>
							</div>
						</div>
					</div>

					{isTNC && <JiliQuestTNC onClose={() => setIsTNC(false)} />}
				</div>
			)}
		</>
	);
}

export default JiliQuest;
